import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { FormApi, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import { BillingDetails, isAccess, useUpdateTransactionAccount } from '@css-xp/base';
import { BillingAdmin, CommerceAddress } from '@css-xp/graphtypes';
import { UpdateTransactionAccountPayload } from '@atlassian/cssxp-types-billing';
import { defaultTo, get, isNil, set, isEmpty } from 'lodash';
import React, { useRef, useState } from 'react';
import { IsoSelectItem } from '@css-xp/base/forms/src/address-form-section';
import UpdateBillingAdminsFields from '@css-xp/base/forms/src/update-billing-admins-fields';
import { Permissions } from '@atlassian/cssxp-typescommon';

interface FormData {
  billToName?: string | null;
  billToAddress1?: string | null;
  billToAddress2?: string | null;
  billToCity?: string | null;
  billToCountry?: IsoSelectItem | null;
  billToPostalCode?: string | null;
  billToState?: IsoSelectItem | null;
  billToTaxId?: string | null;

  shipToName?: string | null;
  shipToAddress1?: string | null;
  shipToAddress2?: string | null;
  shipToCity?: string | null;
  shipToCountry?: IsoSelectItem | null;
  shipToPostalCode?: string | null;
  shipToState?: IsoSelectItem | null;
  shipToTaxId?: string | null;

  recipients?: string[];
}

interface Props {
  transactionAccountId?: string;
  billToAddress?: CommerceAddress | null;
  shipToAddress?: CommerceAddress | null;
  invoiceGroupId?: string;
  billingAdmins?: (BillingAdmin | undefined | null)[];
  isShipToAddressViewable?: boolean | true;
  onClose: () => void;
  /** Logic to run after a failed or successful attempt, such as refreshing data */
  onComplete: () => void;
}

const EditBillingDetailsForm = ({
  transactionAccountId,
  billToAddress,
  shipToAddress,
  invoiceGroupId,
  billingAdmins = [],
  isShipToAddressViewable = true,
  onClose,
  onComplete,
}: Props) => {
  const { isProcessing, updateTransactionAccount } = useUpdateTransactionAccount({ onClose, onComplete });

  const [adminFieldsDirty, setAdminsDirtyFlag] = useState(false);

  // Need this since we can't call hook from the nested function submitBillingDetails.
  const addBillingAdminPermission = useRef(isAccess(Permissions.CONTACT_WRITE));

  // To track if account creation is pending in UpdateBillingAdminsFields and save button should be disabled.
  const [disableSaveButton, setDisableSaveButton] = useState<boolean>(false);

  const updateAdminFieldsDirtyFlag = (flag: boolean) => {
    setAdminsDirtyFlag(flag);
  };

  if (!transactionAccountId) return <p>No transaction account ID found</p>;

  const recipients = !isEmpty(billingAdmins) ? billingAdmins.map((billingAdmin) => billingAdmin?.email) : [''];

  const submitBillingDetails = (billingData: FormData, formApi: FormApi<FormData>) => {
    // @atlaskit/form does not handle default form values well, creating thing like `selectedAddOns[0].selectedAddOn` instead of creating an object at that index. Lodash has our back.
    const transformedData: FormData = {};
    // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
    formApi.getRegisteredFields().forEach((key: any) => {
      // get the value from the field state or the form data
      const value = formApi.getFieldState(key)?.value ?? get(billingData, key);
      set(transformedData, key, value);
    });

    const billingAdminsOnForm = transformedData.recipients ?? [];

    const billingAdminsOnFile = billingAdmins
      // @ts-ignore
      ?.map((item: { email: string }) => item.email)
      .filter((emailOrAaid) => !isNil(emailOrAaid));

    const newAddedBillingAdminsList = billingAdminsOnForm.filter(
      (admin: string) => !billingAdminsOnFile?.includes(admin)
    );

    const deletedBillingAdminsList = billingAdminsOnFile?.filter(
      (admin: string) => !billingAdminsOnForm.includes(admin) && !isNil(admin)
    );

    const updateTransactionAccountPayload: UpdateTransactionAccountPayload = {
      invoiceGroupId,
      billToAddress: {
        name: defaultTo(transformedData.billToName, ''),
        postalAddress: {
          line1: defaultTo(transformedData.billToAddress1, ''),
          line2: defaultTo(transformedData.billToAddress2 || null, null),
          city: defaultTo(transformedData?.billToCity, ''),
          state: defaultTo(transformedData?.billToState?.value, null),
          postcode: defaultTo(transformedData?.billToPostalCode, ''),
          country: defaultTo(transformedData?.billToCountry?.value, ''),
        },
        taxId: transformedData?.billToTaxId || null,
      },

      shipToAddress: {
        id: shipToAddress?.id ?? null,
        name: defaultTo(transformedData.shipToName, ''),
        postalAddress: {
          line1: defaultTo(transformedData.shipToAddress1, ''),
          line2: defaultTo(transformedData?.shipToAddress2 || null, null),
          city: defaultTo(transformedData?.shipToCity, ''),
          state: defaultTo(transformedData?.shipToState?.value, null),
          postcode: defaultTo(transformedData?.shipToPostalCode, ''),
          country: defaultTo(transformedData?.shipToCountry?.value, ''),
        },
        // CCP has strict checks on tax ID format, and won't allow empty strings
        taxId: transformedData.shipToTaxId || null,
      },
      modifiedBillingAdmins: {
        emailsOrAaidsToAdd: addBillingAdminPermission.current ? newAddedBillingAdminsList : [], // to check if their is a permission to add billing admin.
        emailsOrAaidsToDelete: deletedBillingAdminsList,
      },
    };

    updateTransactionAccount({
      transactionAccountId,
      updateTransactionAccountPayload,
    });
  };

  return (
    <div style={{ maxWidth: '600px', padding: '25px' }}>
      <Form onSubmit={submitBillingDetails}>
        {({ formProps, submitting, setFieldValue, dirty }) => (
          <form {...formProps}>
            <FormHeader title="Update transaction account"></FormHeader>
            <FormSection>
              <div style={{ maxWidth: '500px' }}>
                <BillingDetails
                  billToAddress={billToAddress}
                  shipToAddress={shipToAddress}
                  isEditable={true}
                  setFieldValue={setFieldValue}
                  isShipToAddressViewable={isShipToAddressViewable}
                />
                <UpdateBillingAdminsFields
                  recipients={recipients}
                  updateAdminFieldsDirtyFlag={updateAdminFieldsDirtyFlag}
                  disableSaveButton={(status: boolean) => {
                    setDisableSaveButton(status);
                  }}
                  isRequired={true}
                  placeholder={'Atlassian Account email address'}
                  helperMessage={'An active billing account must have atleast 1 billing admin'}
                  title={'Billing admins'}
                  allowToAddBillingAdmin={isAccess(Permissions.CONTACT_WRITE)} // for checking is user has enough permissions to add billign admin
                />
              </div>
            </FormSection>
            <FormFooter align="end">
              <ButtonGroup>
                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isLoading={submitting || isProcessing}
                  isDisabled={(!dirty && !adminFieldsDirty) || disableSaveButton}
                  testId={submitting || isProcessing ? 'loading-button' : 'save-button'}
                >
                  Save
                </LoadingButton>
                <Button appearance="subtle" onClick={onClose}>
                  Close
                </Button>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};

export default EditBillingDetailsForm;
